import { UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { Dialog } from "@headlessui/react";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { AwardIcon, X } from "lucide-react";
import { useEffect, useState } from "react";
import { FaCheckCircle, FaCircle } from "react-icons/fa";

import Button from "./Button";
import {
	useCertifications,
	useSelectedCertification,
	useUserAction,
} from "~/hooks";

const SelectACertificationModal = () => {
	const { userAction, resetUserAction } = useUserAction();
	const { userId } = useAuth();

	const isOpen = userAction === UserAction.SELECT_A_CERTIFICATION && !!userId;

	const {
		selectedCertification,
		handleSelectCertification,
		isLoading: isLoadingSelectedCertification,
	} = useSelectedCertification();

	const [selectedCertificationId, setSelectedCertificationId] =
		useState<string>();

	useEffect(() => {
		selectedCertification &&
			setSelectedCertificationId(selectedCertification._id);
	}, [selectedCertification]);

	const { certifications, isLoading: isLoadingCertifications } =
		useCertifications();
	const isLoading =
		isLoadingCertifications ||
		!certifications ||
		isLoadingSelectedCertification;

	const handleClose = () => {
		resetUserAction();
	};

	if (isLoading) return null;

	return (
		<AnimatePresence mode="wait">
			{isOpen && (
				<Dialog
					key="certification-dialog"
					as={motion.div}
					static
					open={!!isOpen}
					onClose={handleClose}
					className="relative z-50"
				>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1, transition: { duration: 0.3 } }}
						exit={{ opacity: 0, transition: { duration: 0.2 } }}
						className="fixed inset-0 z-50 bg-black/40 dark:bg-black/50"
					/>

					<motion.div className="fixed inset-0 z-50 overflow-y-auto">
						<form
							className="flex min-h-full items-end justify-center sm:items-center sm:p-10"
							onSubmit={async (e) => {
								e.preventDefault();

								await handleSelectCertification(
									certifications?.find(
										(cert) => cert._id === selectedCertificationId,
									)!,
								);

								handleClose();
							}}
						>
							<Dialog.Panel
								as={motion.div}
								initial={{ opacity: 0, y: 15 }}
								animate={{ opacity: 1, y: 0, transition: { duration: 0.2 } }}
								exit={{ opacity: 0, y: 15, transition: { duration: 0.15 } }}
								className="relative mx-auto flex w-full max-w-[30rem] flex-col gap-5 rounded-t-xl bg-white px-4 pt-4 pb-10 shadow-2xl shadow-black/10 sm:gap-5 sm:rounded-2xl sm:px-8 sm:pt-6 sm:pb-10 dark:bg-slate-800 dark:shadow-slate-900"
							>
								<div className="flex items-start justify-between">
									<Dialog.Title className="font-semibold phablet:text-2xl text-xl">
										Select Your Certification
									</Dialog.Title>
									<button
										className="relative right-0 flex cursor-pointer rounded-full bg-slate-100 p-1 transition-all hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600"
										onClick={handleClose}
									>
										<X className="h-5 phablet:h-6 phablet:w-6 w-5" />
									</button>
								</div>

								<div className="flex flex-col gap-3 sm:gap-4">
									{certifications?.map((certification) => (
										<button
											key={certification._id}
											className={clsx(
												`flex cursor-pointer items-center gap-4 rounded-xl border-2 border-slate-200 px-3.5 pt-2 pb-2.5 text-left transition-all duration-200 sm:px-5 sm:py-3 dark:border-slate-600`,
												{
													"!border-emerald-400 dark:!border-emerald-600 shadow-slate-100 shadow-xl dark:shadow-slate-900":
														selectedCertificationId === certification._id,
												},
												{
													"pointer-events-none opacity-50":
														certification.outOfScope,
												},
											)}
											onClick={() => {
												!certification.outOfScope &&
													setSelectedCertificationId(certification._id);
											}}
										>
											<div className="flex flex-1 flex-col gap-0.5 sm:gap-1">
												{!["courses", "not-sure"].includes(
													certification._id,
												) && (
													<div className="flex items-center gap-4">
														<p className="mt-0.5 phablet:mt-0 -mb-0.5 flex items-center gap-1 font-medium text-orange-700 text-sm dark:text-orange-300">
															<AwardIcon size={16} strokeWidth={2.4} />{" "}
															Certification
														</p>

														{certification.outOfScope && (
															<p className="-mb-1 font-medium text-red-700 text-sm dark:text-red-400">
																Out Of Scope
															</p>
														)}
													</div>
												)}

												<h2 className="flex items-center gap-2 font-medium text-lg sm:text-xl">
													{certification.title}
												</h2>

												<p className="font-medium text-slate-700 text-sm dark:text-slate-200">
													{certification.focus}
												</p>
											</div>

											<div className="flex flex-col justify-center gap-4">
												<AnimatePresence mode="wait">
													{selectedCertificationId === certification._id ? (
														<motion.div
															initial={{ scale: 0.6 }}
															animate={{ scale: 1 }}
															transition={{ duration: 0.2 }}
															key="check"
														>
															<FaCheckCircle className="text-emerald-500 text-xl" />
														</motion.div>
													) : (
														<motion.div
															key="uncheck"
															exit={{
																scale: 0.6,
															}}
															transition={{ duration: 0.2 }}
														>
															<FaCircle className="rounded-full border-2 text-white text-xl dark:border-slate-600 dark:text-slate-800" />
														</motion.div>
													)}
												</AnimatePresence>
											</div>
										</button>
									))}
								</div>

								<Button
									variant={"filled"}
									type="submit"
									disabled={
										!selectedCertificationId ||
										selectedCertification?._id === selectedCertificationId
									}
								>
									Confirm Changes
								</Button>
							</Dialog.Panel>
						</form>
					</motion.div>
				</Dialog>
			)}
		</AnimatePresence>
	);
};

export default SelectACertificationModal;
